import { List, useTable } from '@refinedev/antd';
import { ListTotal } from '../../components/table/ListTotal';
import { useCan, usePermissions } from '@refinedev/core';
import { Table, Input, Tag, Space, Select, Result, Spin, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { hasPermission } from '../../utils/permissions';
import { Link, useNavigate } from 'react-router-dom';
import type { CrudFilters } from '@refinedev/core';
import {
  ACCOUNT_TYPE_PICKER_OPTIONS,
  COMPANIES_META,
  accountTypeLabel,
  SALES_CHANNELS,
  SALES_CHANNEL_LABELS,
  salesChannelLabel,
  type AccountType,
  type SalesChannel,
} from './constants';
import { createdByLabel, type CompanyCreator } from './companyCreator';
import { COMPANY_DELETE_PERMISSION, type CompanyDeletionRow } from './api/companyDeleteClient';
import { useEditableCompanies } from './api/editableCompaniesClient';
import { batchTickBlock, companyId } from './companyBatch';
import { CompaniesBatchBar } from './components/CompaniesBatchBar';
import { CompaniesTabs } from './components/CompaniesTabs';
import { ServeFirstLiveTag } from './components/ServeFirstLiveTag';
import {
  USERS_READ_PERMISSION,
  useSuperAdminCounts,
  usersWithAccessPath,
} from './api/superAdminCountsClient';

/** SF-5055 — the web app's help desk plans (sf-react-app companyHelpDeskPlans). */
export type HelpDeskPlan = 'premium' | 'lite' | 'mix';
/** SF-5126 — how emails become help desk tickets for a company. */
export type HelpDeskEmailMode = 'off' | 'forwarding';

export interface Company {
  _id?: string;
  id?: string;
  name: string;
  internal_name?: string;
  is_active?: boolean;
  account_type?: AccountType;
  /** SF-4578 — missing on companies saved before the field existed (treated as enterprise). */
  sales_channel?: SalesChannel;
  // Whether the company has active Eva usage in the current calendar month
  // (server-derived from EvaUsageMonthly on GET /admin/v1/companies). Not shown
  // on this primary list — it's rendered by the Eva overview's All Companies
  // table (SF-3875). Lives on the shared interface so both consumers agree.
  has_active_eva_usage?: boolean;
  color?: string;
  privacy_link?: string;
  createdAt?: string;
  // SF-4324 — set to 'deleting' while an async hard-delete purge runs. Rows in
  // this state are badged and not clickable (a half-purged company must not be
  // navigable as a normal record).
  // SF-4813: 'scheduled' while the company waits for its deletion date: it is
  // switched off but still a normal record, so it stays clickable with a tag.
  deletion_status?: string | null;
  /** SF-4813: the open deletion (detail read only); null when there is none. */
  deletion?: CompanyDeletionRow | null;
  /** SF-4813: days between a deletion request and the deletion (detail read only). */
  deletion_grace_days?: number | null;
  // SF-4343 — "Use custom sending domain" (chargeable CSAT add-on). Off by
  // default; toggled from the edit page, saved via the admin company PATCH.
  csat_custom_domain_enabled?: boolean;
  /** Where unpinned links/emails go while the add-on is on: the default domain, or the platform. */
  csat_default_link_host?: 'custom' | 'platform';
  // SF-4576 — external AI access (Claude, ChatGPT over MCP). Off by default;
  // switched from the detail page on its own, never by the Edit form.
  mcp_access?: boolean;
  /** SF-4656 — SSO integration feature flag (admin PATCH only, admin:sso:write). */
  sso_enabled?: boolean;
  /** SF-4677 — Eva triages new helpdesk tickets and writes replies. Off by default. */
  eva_helpdesk_replies_enabled?: boolean;
  /** SF-5081 — `manual` (staff ask), `draft` (automatic drafts) or `auto_send`. Missing = manual. */
  eva_helpdesk_reply_mode?: 'manual' | 'draft' | 'auto_send';
  /** SF-4677 — Eva's sign-off line; empty = "<company name> Customer Care" (sf-api). */
  eva_helpdesk_reply_signoff?: string;
  /**
   * SF-5055 — days back an earlier ticket from the same email makes the customer a returning one
   * (Eva then never sends herself). 1..365; missing on older companies = 30.
   */
  eva_helpdesk_repeat_window_days?: number;
  /** SF-5082 — what high / medium / low priority mean for this company. Blank = today's rule. */
  eva_helpdesk_priority_guide?: { high?: string; medium?: string; low?: string };
  /**
   * SF-5082 — what Eva may send on her own at the auto_send level (see evaAutoSendOptions.ts).
   * Missing = the default; an empty list = none.
   */
  eva_helpdesk_auto_send_priorities?: string[];
  eva_helpdesk_auto_send_reply_types?: string[];
  eva_helpdesk_auto_send_categories?: string[];
  /** SF-5055 — the web app's help desk plan. Missing = Lite (sf-api's default). */
  helpDeskPlan?: HelpDeskPlan;
  /**
   * The company's own support address. SF-5126: shown for reference only; tickets come from
   * emails that arrive at the forwarding address. Empty = none.
   */
  assets_support_email?: string;
  /** SF-5126 — whether emails create help desk tickets (saved on its own). Missing = off. */
  help_desk_email_mode?: HelpDeskEmailMode;
  /** SF-5126 — where the company forwards its support mailbox; made by sf-api. null = none yet. */
  help_desk_forwarding_address?: string | null;
  /** SF-5055 — low ratings create help desk tickets (the web app's company edit switch). */
  allowRatingHelpdesk?: boolean;
  /**
   * SF-5055 — the company's Help Desk module switch in the web app (detail read only; PATCHed on
   * its own). null when sf-api could not read it.
   */
  help_desk_module_enabled?: boolean | null;
  /** SF-4770: opens the web app in this company (built by sf-api on the detail read); null when not set up. */
  web_app_link?: string | null;
  /** SF-4779: everyone in the company who signs in with a password must use two-step sign-in. */
  twoFactorAuthentication?: boolean;
  /** SF-4799: who created the company (sf-api, from the change log); null when not recorded. */
  created_by?: CompanyCreator | null;
  /** ServeFirst Forms company (an online sign-up). */
  forms_account?: boolean;
}

const ACCOUNT_TYPE_OPTIONS = [
  { label: 'All account types', value: '' },
  ...ACCOUNT_TYPE_PICKER_OPTIONS,
];

const SALES_CHANNEL_OPTIONS = [
  { label: 'All sales channels', value: '' },
  ...SALES_CHANNELS.map((c) => ({ label: SALES_CHANNEL_LABELS[c], value: c })),
];

// SF-4875: the list opens on active paying customers. A link that carries its own filters
// (back navigation, a shared link) opens with those instead.
const DEFAULT_COMPANY_FILTERS: CrudFilters = [
  { field: 'is_active', operator: 'eq', value: 'true' },
  { field: 'account_type', operator: 'eq', value: 'Paid' },
];

const SUPER_ADMINS_HELP =
  'Every web app super admin who can see this company, with or without an admin app ' +
  'account: people with it on their company list, plus people who see every company.';

const ACTIVE_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Active only', value: 'true' },
  { label: 'Inactive only', value: 'false' },
];

export const CompaniesListPage: React.FC = () => {
  const navigate = useNavigate();

  const { data: canList, isLoading: canListLoading } = useCan({
    resource: 'companies',
    action: 'list',
  });

  // SF-4940: batch actions. Account type needs company edit rights, deletion the delete
  // permission (the danger zone's rule); both only on companies the person may edit.
  // SF-5158: Mark inactive / active need the same edit rights as the company page's Active switch.
  const { data: canEdit } = useCan({ resource: 'companies', action: 'edit' });
  const { data: permissions } = usePermissions<string[]>();
  const canSetType = canEdit?.can === true;
  const canDelete = hasPermission(permissions ?? [], COMPANY_DELETE_PERMISSION);
  const canBatch = canSetType || canDelete;
  // SF-4996: the Super admins column reads the sign-in service's users, so it needs users read.
  const canReadUsers = hasPermission(permissions ?? [], USERS_READ_PERMISSION);
  const editableQuery = useEditableCompanies();
  const editScope = editableQuery.data?.data;
  // Ticked companies as they were when ticked, kept across pages and filters until cleared.
  const [selected, setSelected] = useState<Map<string, Company>>(() => new Map());

  const handleSelectionChange = (keys: React.Key[], rows: (Company | undefined)[]) => {
    setSelected((prev) => {
      const next = new Map<string, Company>();
      const byId = new Map(
        rows.filter(Boolean).map((r) => [companyId(r as Company), r as Company]),
      );
      keys.map(String).forEach((key) => {
        const row = byId.get(key) ?? prev.get(key);
        if (row) next.set(key, row);
      });
      return next;
    });
  };

  const untick = (ids: string[]) =>
    setSelected((prev) => {
      const next = new Map(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });

  const {
    tableProps,
    setFilters,
    setCurrent,
    filters: currentFilters,
    tableQuery,
  } = useTable<Company>({
    resource: 'companies',
    meta: COMPANIES_META,
    syncWithLocation: true,
    pagination: { pageSize: 20 },
    filters: { initial: DEFAULT_COMPANY_FILTERS },
    queryOptions: { enabled: canList?.can === true },
  });

  const visibleIds = (tableProps.dataSource ?? []).map(companyId).filter(Boolean);
  const countsQuery = useSuperAdminCounts(visibleIds, canReadUsers);

  const renderSuperAdmins = (_: unknown, record: Company) => {
    if (countsQuery.isLoading) return <Spin size="small" aria-label="Loading super admins" />;
    const count = countsQuery.isError
      ? undefined
      : countsQuery.data?.get(companyId(record).toLowerCase());
    if (!count) return <Typography.Text type="secondary">Couldn&apos;t load</Typography.Text>;
    return (
      <Space size={4} direction="vertical">
        <Link
          to={usersWithAccessPath(companyId(record))}
          // The row opens the company; this link opens the Users page instead.
          onClick={(event) => event.stopPropagation()}
        >
          {count.list + count.all}
        </Link>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {count.list} on list
        </Typography.Text>
      </Space>
    );
  };

  // Initialise local filter controls from URL so back-navigation and shared
  // links pre-populate the inputs rather than showing stale empty defaults.
  const getFilterVal = (field: string): string => {
    const f = currentFilters?.find((item) => 'field' in item && item.field === field);
    return f && 'value' in f ? ((f.value as string) ?? '') : '';
  };

  const [searchValue, setSearchValue] = useState(() => getFilterVal('search'));
  // The dropdowns show the table's own filters, so they stay right when the table resets
  // them (opening the plain list again from the tabs brings back the defaults).
  const activeFilter = getFilterVal('is_active');
  const accountTypeFilter = getFilterVal('account_type');
  const salesChannelFilter = getFilterVal('sales_channel');

  const applyFilters = (opts: {
    search?: string;
    isActive?: string;
    accountType?: string;
    salesChannel?: string;
  }) => {
    const search = opts.search ?? searchValue;
    const isActive = opts.isActive ?? activeFilter;
    const accountType = opts.accountType ?? accountTypeFilter;
    const salesChannel = opts.salesChannel ?? salesChannelFilter;

    const filters: CrudFilters = [];
    if (search) filters.push({ field: 'search', operator: 'contains', value: search });
    // SF-4875: these two have defaults, so "All" is kept as an empty value instead of being
    // dropped. With no filters in the address the list would open on the defaults again
    // (after Back, or a reload). sf-api reads an empty value as no filter.
    filters.push({ field: 'is_active', operator: 'eq', value: isActive });
    filters.push({ field: 'account_type', operator: 'eq', value: accountType });
    if (salesChannel) filters.push({ field: 'sales_channel', operator: 'eq', value: salesChannel });
    // A narrower filter may have fewer pages than the one being viewed.
    setCurrent(1);
    setFilters(filters, 'replace');
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    applyFilters({ search: value });
  };

  const handleActiveChange = (value: string) => {
    applyFilters({ isActive: value });
  };

  const handleAccountTypeChange = (value: string) => {
    applyFilters({ accountType: value });
  };

  const handleSalesChannelChange = (value: string) => {
    applyFilters({ salesChannel: value });
  };

  if (canListLoading) return null;
  if (!canList?.can) {
    return (
      <Result
        status="403"
        title="Not Authorized"
        subTitle={canList?.reason ?? 'You do not have permission to view companies.'}
      />
    );
  }

  return (
    <List title="Companies">
      <CompaniesTabs active="all" />
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space wrap>
          <Input
            placeholder="Search name or internal name..."
            prefix={<SearchOutlined />}
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
            style={{ width: 320 }}
          />
          <Select
            aria-label="Active filter"
            value={activeFilter}
            onChange={handleActiveChange}
            options={ACTIVE_OPTIONS}
            style={{ width: 160 }}
          />
          <Select
            aria-label="Account type filter"
            value={accountTypeFilter}
            onChange={handleAccountTypeChange}
            options={ACCOUNT_TYPE_OPTIONS}
            style={{ width: 200 }}
          />
          <Select
            aria-label="Sales channel filter"
            value={salesChannelFilter}
            onChange={handleSalesChannelChange}
            options={SALES_CHANNEL_OPTIONS}
            style={{ width: 200 }}
          />
          <ListTotal query={tableQuery} singular="company" plural="companies" />
        </Space>
        {canBatch && (
          <CompaniesBatchBar
            selected={[...selected.values()]}
            canSetType={canSetType}
            canSetActive={canSetType}
            canDelete={canDelete}
            onDone={untick}
            onClear={() => setSelected(new Map())}
          />
        )}
        <Table
          {...tableProps}
          rowKey={(record) => record._id || record.id || record.internal_name || record.name}
          rowSelection={
            canBatch
              ? {
                  selectedRowKeys: [...selected.keys()],
                  onChange: handleSelectionChange,
                  // Keep ticks on other pages when moving between pages or filtering.
                  preserveSelectedRowKeys: true,
                  getCheckboxProps: (record: Company) => {
                    const block = batchTickBlock(record, editScope);
                    return {
                      disabled: block !== null,
                      title: block ?? undefined,
                      'aria-label': `Select ${record.name}`,
                    };
                  },
                }
              : undefined
          }
          onRow={(record) => {
            // SF-4324: a company mid-purge is not a normal record — don't let
            // the operator click through to a detail page that's being erased.
            if (record.deletion_status === 'deleting') {
              return { style: { cursor: 'not-allowed', opacity: 0.5 } };
            }
            return {
              onClick: (event: React.MouseEvent) => {
                // Ticking the box (or just missing it) must not open the company.
                if ((event.target as HTMLElement).closest?.('.ant-table-selection-column')) return;
                navigate(`/companies/${record._id || record.id}`);
              },
              style: { cursor: 'pointer' },
            };
          }}
        >
          <Table.Column<Company>
            dataIndex="name"
            title="Name"
            sorter
            sortDirections={['ascend']}
            render={(name: string, record) => (
              <Space>
                {name}
                <ServeFirstLiveTag accountType={record.account_type} />
                {record.deletion_status === 'deleting' ? <Tag color="red">Deleting</Tag> : null}
                {record.deletion_status === 'scheduled' ? (
                  <Tag color="orange">Pending deletion</Tag>
                ) : null}
              </Space>
            )}
          />
          <Table.Column
            dataIndex="internal_name"
            title="Internal Name"
            render={(v?: string) => v || '—'}
          />
          <Table.Column
            dataIndex="is_active"
            title="Active"
            sorter
            sortDirections={['ascend']}
            render={(active?: boolean) => (
              <Tag color={active ? 'green' : 'default'}>{active ? 'Active' : 'Inactive'}</Tag>
            )}
          />
          <Table.Column
            dataIndex="account_type"
            title="Account Type"
            render={(v?: string) => (v ? <Tag color="blue">{accountTypeLabel(v)}</Tag> : '—')}
          />
          <Table.Column
            dataIndex="sales_channel"
            title="Sales Channel"
            render={(v?: string) => <Tag>{salesChannelLabel(v)}</Tag>}
          />
          <Table.Column
            dataIndex="createdAt"
            title="Created At"
            sorter
            sortDirections={['ascend']}
            render={(date?: string) => (date ? new Date(date).toLocaleDateString() : '—')}
          />
          {canReadUsers && (
            <Table.Column<Company>
              key="super_admins"
              title={
                <Tooltip title={SUPER_ADMINS_HELP}>
                  <Space size={4}>
                    Super admins
                    <QuestionCircleOutlined aria-label="About super admins" />
                  </Space>
                </Tooltip>
              }
              render={renderSuperAdmins}
            />
          )}
          <Table.Column<Company>
            key="created_by"
            title="Created By"
            render={(_: unknown, record) => createdByLabel(record)}
          />
        </Table>
      </Space>
    </List>
  );
};
